/**
 * Outil « Horloge des durées » — calculs (pur).
 *
 * Une heure de la journée est un nombre de minutes depuis minuit (0 à 1 439).
 * Une durée entre deux heures se compte en trois sauts, comme au tableau :
 * jusqu'à l'heure pile, les heures entières, puis les minutes qui restent.
 * Si l'arrivée est avant le départ, on arrive le lendemain (trajet de nuit).
 * Une heure fait 60 minutes, pas 100 : la soustraction posée « emprunte »
 * 1 h = 60 min, et le calcul « en décimaux » (10,20 − 8,45) est faux.
 */

import { rational, type ExactNumber } from '../../ui/exact';

export const DAY = 24 * 60;

/** heure et minute d'un instant (ramené dans la journée) */
export function hm(t: number): { h: number; m: number } {
  const x = ((Math.round(t) % DAY) + DAY) % DAY;
  return { h: Math.floor(x / 60), m: x % 60 };
}

/** « 8 h 45 », « 9 h », « 0 h 05 » */
export function timeText(t: number): string {
  const { h, m } = hm(t);
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')}`;
}

/** « 1 h 35 min », « 45 min », « 2 h », « 0 min » */
export function durationText(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/** Durée de `start` à `end` ; arrivée avant le départ = le lendemain. */
export function durationBetween(start: number, end: number): number {
  return (((end - start) % DAY) + DAY) % DAY;
}

export function nextDay(start: number, end: number): boolean {
  return end < start;
}

export interface Step {
  /** instants absolus (peuvent dépasser minuit : 1 500 = 1 h le lendemain) */
  from: number;
  to: number;
  minutes: number;
  kind: 'toHour' | 'hours' | 'rest';
}

/** Les sauts de la frise : jusqu'à l'heure pile, heures entières, minutes restantes. */
export function steps(start: number, end: number): Step[] {
  const d = durationBetween(start, end);
  if (d === 0) return [];
  const s = start;
  const e = start + d;
  const firstHour = Math.ceil(s / 60) * 60;
  if (firstHour >= e) {
    // tout tient dans la même heure (ou on s'arrête pile à l'heure)
    return [{ from: s, to: e, minutes: d, kind: firstHour === e ? 'toHour' : 'rest' }];
  }
  const lastHour = Math.floor(e / 60) * 60;
  const out: Step[] = [];
  if (firstHour > s) out.push({ from: s, to: firstHour, minutes: firstHour - s, kind: 'toHour' });
  if (lastHour > firstHour)
    out.push({ from: firstHour, to: lastHour, minutes: lastHour - firstHour, kind: 'hours' });
  if (e > lastHour) out.push({ from: lastHour, to: e, minutes: e - lastHour, kind: 'rest' });
  return out;
}

/** Heure d'arrivée : départ + durée (et le nombre de jours passés). */
export function addDuration(start: number, dur: number): { end: number; days: number } {
  const t = start + dur;
  return { end: ((t % DAY) + DAY) % DAY, days: Math.floor(t / DAY) };
}

export interface Subtraction {
  /** arrivée écrite en h et min (avec + 24 h si on arrive le lendemain) */
  top: { h: number; m: number };
  /** la même après l'emprunt de 1 h = 60 min (null s'il n'en faut pas) */
  borrowed: { h: number; m: number } | null;
  bottom: { h: number; m: number };
  result: { h: number; m: number };
}

/** Soustraction posée « arrivée − départ », avec l'emprunt 1 h = 60 min. */
export function postedSubtraction(start: number, end: number): Subtraction {
  const a = hm(end);
  const b = hm(start);
  const top = { h: a.h + (nextDay(start, end) ? 24 : 0), m: a.m };
  const borrowed = top.m < b.m ? { h: top.h - 1, m: top.m + 60 } : null;
  const t = borrowed ?? top;
  return { top, borrowed, bottom: b, result: { h: t.h - b.h, m: t.m - b.m } };
}

/**
 * Le piège : calculer « 10,20 − 8,45 » comme des nombres décimaux. Renvoie
 * ce faux calcul quand il ne donne PAS la bonne durée (emprunt nécessaire).
 */
export function decimalTrap(start: number, end: number): { text: string; wrong: string } | null {
  const s = postedSubtraction(start, end);
  if (!s.borrowed) return null;
  const a = s.top.h * 100 + s.top.m;
  const b = s.bottom.h * 100 + s.bottom.m;
  const r = a - b;
  const fmt = (x: number) => `${Math.floor(x / 100)},${String(x % 100).padStart(2, '0')}`;
  return { text: `${fmt(a)} − ${fmt(b)} = ${fmt(r)}`, wrong: fmt(r) };
}

/** Durée en heures : 90 min = 1,5 h ; 95 min = 19/12 h. */
export function inHours(min: number): ExactNumber {
  return rational(min, 60);
}

/** Minute (0-59) pointée par l'aiguille des minutes à l'angle `deg` (0 = midi, sens horaire). */
export function minuteAtAngle(deg: number, step = 5): number {
  const a = ((deg % 360) + 360) % 360;
  const m = Math.round(a / 6 / step) * step;
  return m % 60;
}

/**
 * Nouvel instant quand on tourne l'aiguille des minutes de `t` vers `minute` :
 * passer midi en avançant ajoute une heure, en reculant en retire une.
 */
export function turnMinuteHand(t: number, minute: number): number {
  const { h, m } = hm(t);
  let hour = h;
  if (m >= 45 && minute < 15) hour += 1;
  else if (m < 15 && minute >= 45) hour -= 1;
  return (((hour * 60 + minute) % DAY) + DAY) % DAY;
}

/**
 * Nouvel instant quand on pose l'aiguille des heures à l'angle `deg` : des
 * deux heures possibles (h et h + 12), on garde la plus proche de l'heure
 * actuelle — on passe midi et minuit en tournant, sans sauter de 12 h.
 */
export function turnHourHand(t: number, deg: number): number {
  const { m } = hm(t);
  const a = ((deg % 360) + 360) % 360;
  let h12 = Math.round(a / 30 - m / 60);
  h12 = ((h12 % 12) + 12) % 12;
  const dist = (x: number) => {
    const d = Math.abs(x - t) % DAY;
    return Math.min(d, DAY - d);
  };
  const c1 = h12 * 60 + m;
  const c2 = (h12 + 12) * 60 + m;
  return dist(c1) <= dist(c2) ? c1 : c2;
}

/** Angles des aiguilles (degrés, 0 = midi, sens horaire). */
export function handAngles(t: number): { hour: number; minute: number } {
  const { h, m } = hm(t);
  return { hour: ((h % 12) + m / 60) * 30, minute: m * 6 };
}

/** Addition posée « départ + durée », avec la retenue 60 min = 1 h. */
export function postedAddition(
  start: number,
  dur: number,
): {
  sum: { h: number; m: number };
  carried: { h: number; m: number } | null;
  days: number;
} {
  const a = hm(start);
  const sum = { h: a.h + Math.floor(dur / 60), m: a.m + (dur % 60) };
  const carried = sum.m >= 60 ? { h: sum.h + 1, m: sum.m - 60 } : null;
  const final = carried ?? sum;
  return { sum, carried, days: Math.floor(final.h / 24) };
}

/** Défis « combien de temps entre… » (départ, arrivée), du plus simple au trajet de nuit. */
export const CHALLENGES: ReadonlyArray<readonly [number, number]> = [
  [8 * 60 + 30, 10 * 60],
  [9 * 60 + 50, 11 * 60 + 15],
  [13 * 60 + 40, 14 * 60 + 25],
  [7 * 60 + 45, 12 * 60 + 10],
  [10 * 60 + 35, 12 * 60 + 5],
  [15 * 60 + 55, 18 * 60 + 20],
  [6 * 60 + 25, 9 * 60 + 5],
  [22 * 60 + 40, 1 * 60 + 25],
];
